import { makeStyles } from '@material-ui/styles'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import GenerateSupportBundleModal from 'app/plugins/openstack/components/infrastructure/hosts/GenerateSupportBundleModal'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import DocumentMeta from 'core/components/DocumentMeta'
import PollingData, { POLLING_INTERVALS } from 'core/components/PollingData'
import SimpleLink from 'core/components/SimpleLink'
import ListContainer from 'core/containers/ListContainer'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import UsageProgress from 'k8s/components/capacity-and-health/UsageProgress'
import DataKeys from 'k8s/DataKeys'
import { pick } from 'ramda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { formatTimestampToLocal } from '../../vms/helpers'
import { listHostAggregates } from '../actions'
import { hostAggregatesSelector } from '../selectors'
import { deleteClusterHostGroup, listClusterHostGroups, listClusterVMHAStatus } from './actions'
import CreateClusterHostGroupsModal from './CreateClusterHostGroupsModal'
import DeleteClusterHostGroupsDialog from './DeleteClusterHostGroupsDialog'
import EditClusterHostGroupModal from './EditClusterHostGroupModal'
import { clusterHostGroupsSelector, clusterVMHAStatusSelector } from './selectors'
import { VMHAStatusForClusterList } from './VMHAStatusCell'

type ModelDataKey = DataKeys.ClusterHostGroups
type SelectorModel = ArrayElement<ReturnType<typeof clusterHostGroupsSelector>>
type ActionParams = InferActionParams<typeof listClusterHostGroups>

type Params = ActionParams & {}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'ClusterHostGroups',
  listTablePrefs,
)

const defaultParams: Params & SortingState = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

// This component is used to display the enabled or disabled status of a cluster host group
export const EnabledOrDisabled = (props) => {
  const classes = useStyles({ enabled: props.value })
  return (
    <Text variant="body2" className={classes.circle}>
      {props.value ? 'Enabled' : 'Disabled'}
    </Text>
  )
}

const modeString = {
  passthrough: 'Passthrough',
  vgpu: 'vGPU',
}

export const GpuCellComponent = ({ value, item }) => {
  const classes = useStyles({ enabled: value })
  return (
    <>
      <Text variant="body2" className={classes.circle}>
        {value ? `Enabled (${modeString[item?.gpu?.mode]})` : 'Disabled'}
      </Text>
    </>
  )
}

export const modeLabels = {
  custom: 'Custom',
  'host-model': 'Host Model',
  'host-passthrough': 'Host Passthrough',
}

const ClusterHostGroupsListPage = () => {
  const [showEditClusterHostGroupModal, setShowEditClusterHostGroupModal] = useState<boolean>(false)
  const [selectedClusterHostGroup, setSelectedClusterHostGroup] = useState<SelectorModel[] | null>(
    null,
  )
  const [showDeleteClusterHostGroupModal, setShowDeleteClusterHostGroupModal] = useState<boolean>(
    false,
  )
  const [showDownloadSupportBundleModal, setShowDownloadSupportBundleModal] = useState<boolean>(
    false,
  )
  const { params, getParamsUpdater } = usePrefParams(defaultParams)

  const { loading, reload, refreshing: clusterHostGroupsRefreshing } = useListAction(
    listClusterHostGroups,
  )
  const clusterHostGroups = useSelector(clusterHostGroupsSelector)
  const isVMHAEnabledForAnyCluster = useMemo(
    () => clusterHostGroups.some((cluster) => cluster?.vmHighAvailability?.enabled),
    [clusterHostGroups],
  )

  const { loading: loadingClusterVMHAStatus, reload: loadClusterVMHAStatus } = useListAction(
    listClusterVMHAStatus,
    {
      loadOnDemand: true,
      params: { clusters: clusterHostGroups },
    },
  )
  const clusterVMHAStatus = useSelector(clusterVMHAStatusSelector)

  const { reload: hostAggregatesReload } = useListAction(listHostAggregates, {
    params,
  })
  const hostAggregates = useSelector(hostAggregatesSelector)

  // Load Cluster VMHA Status
  useEffect(() => {
    if (
      clusterHostGroups?.length === 0 ||
      clusterVMHAStatus?.length > 0 ||
      !isVMHAEnabledForAnyCluster ||
      loading ||
      loadingClusterVMHAStatus
    ) {
      return
    }

    loadClusterVMHAStatus()
  }, [
    clusterHostGroups,
    clusterVMHAStatus,
    isVMHAEnabledForAnyCluster,
    loading,
    loadingClusterVMHAStatus,
  ])

  const columns: GridViewColumn<SelectorModel>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Name',
        CellComponent: ({ value, item }) => (
          <SimpleLink src={routeManager.routes.openstack.clusterDetails.path({ id: item?.name })}>
            {((<>{value}</>) as unknown) as React.ReactNode}
          </SimpleLink>
        ),
      },
      // {
      //   key: 'status',
      //   label: 'Status',
      //   CellComponent: ({ value, item }) => (
      //     <Badge variant={getClusterHostGroupBadgeVariant(item?.status)} text={value} />
      //   ),
      // },
      {
        key: 'hostlist.length',
        label: 'Hosts',
        CellComponent: ({ value = '0' }) => <Text variant="body2">{value as string}</Text>,
      },
      {
        key: 'physicalNetworkLabels',
        label: 'Physical Network Label',
        display: false,
        CellComponent: (props) => {
          const physicalNetworkLabels = (props?.value as string[]) || []
          return <Text variant="body2"> {physicalNetworkLabels?.join(', ')}</Text>
        },
      },
      {
        key: 'vmhaStatus',
        label: 'VMHA',
        CellComponent: ({ value, item }) => (
          <VMHAStatusForClusterList
            value={(value || '') as string}
            item={item}
            loading={loadingClusterVMHAStatus}
          />
        ),
      },
      {
        key: 'autoResourceRebalancing.enabled',
        label: 'Dynamic Resource Rebalancing',
        CellComponent: EnabledOrDisabled,
      },
      {
        key: 'gpu.enabled',
        label: 'GPU',
        CellComponent: GpuCellComponent,
      },
      {
        key: 'cpu.mode',
        label: 'Virtualized Guest CPU Mode',
        render: (value: any) => {
          return modeLabels[value] || 'Default'
        },
        display: false,
      },
      {
        key: 'cpu.model',
        label: 'Virtualized Guest CPU Model',
        display: false,
      },
      {
        key: 'totalUsage.cpuUsage',
        label: 'CPU Usage (GHz)',
        CellComponent: UsageProgress,
      },
      {
        key: 'totalUsage.memoryUsage',
        label: 'Memory Usage (GiB)',
        CellComponent: UsageProgress,
      },
      {
        key: 'totalUsage.storageUsage',
        label: 'Ephemeral Storage Usage (GiB)',
        CellComponent: UsageProgress,
      },
      {
        key: 'created_at',
        label: 'Created',
        render: (time: string) => formatTimestampToLocal(time),
        display: false,
      },
    ],
    [loadingClusterVMHAStatus],
  )

  const batchActions = [
    {
      cond: (selectedItems) => selectedItems.length === 1,
      disabledTooltip: 'You can only edit one cluster at a time',
      label: 'Edit Cluster',
      icon: 'edit',
      handleAction: (selectedItems) => {
        setSelectedClusterHostGroup(selectedItems)
        setShowEditClusterHostGroupModal(true)
      },
      refreshAfterSuccess: true,
    },
    {
      label: 'Generate Support Bundle',
      icon: 'block-question',
      handleAction: (selectedItems) => {
        setSelectedClusterHostGroup(selectedItems)
        setShowDownloadSupportBundleModal(true)
      },
      refreshAfterSuccess: true,
      disabledTooltip: 'You can only download support bundle for one cluster at a time',
    },
  ]

  const handlePolling = useCallback(
    () =>
      Promise.resolve().then(() => {
        reload(true, false)
      }),
    [reload],
  )

  const handleReload = useCallback(() => {
    reload(true, true)
    hostAggregatesReload(true, true)
    loadClusterVMHAStatus(true, true)
  }, [reload, hostAggregatesReload])

  return (
    <>
      <DocumentMeta title="Clusters" />
      <PollingData
        hidden
        loading={loading}
        onReload={handlePolling}
        refreshDuration={POLLING_INTERVALS.THIRTY_SECONDS}
      />
      <CreateClusterHostGroupsModal
        addRoute={routeManager.routes.openstack.createCluster}
        existingClusterHostGroups={clusterHostGroups}
        existingHostAggregates={hostAggregates}
        reload={handleReload}
      />
      {showEditClusterHostGroupModal && (
        <EditClusterHostGroupModal
          onClose={() => setShowEditClusterHostGroupModal(false)}
          rows={selectedClusterHostGroup}
        />
      )}
      {showDeleteClusterHostGroupModal && (
        <DeleteClusterHostGroupsDialog
          onClose={() => setShowDeleteClusterHostGroupModal(false)}
          rows={selectedClusterHostGroup}
        />
      )}
      {showDownloadSupportBundleModal && (
        <GenerateSupportBundleModal
          onClose={() => setShowDownloadSupportBundleModal(false)}
          rows={selectedClusterHostGroup}
        />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        searchTargets={['name', 'vmhaStatus']}
        uniqueIdentifier="name"
        loading={loading}
        loadingMessage="Loading Clusters..."
        onRefresh={handleReload}
        deleteAction={deleteClusterHostGroup}
        deleteCond={(clusterHostGroups) =>
          !clusterHostGroups.some((clusterHostGroup) => clusterHostGroup?.hostlist?.length > 0)
        }
        DeleteDialogComponent={DeleteClusterHostGroupsDialog}
        deleteDisabledInfo="You cannot delete clusters which have hosts assigned to them"
        postDeleteAction={handleReload}
        refreshing={clusterHostGroupsRefreshing}
        multiSelection
        data={clusterHostGroups}
        columns={columns}
        getParamsUpdater={getParamsUpdater}
        label="Clusters"
        showItemsCountInLabel
        batchActions={batchActions}
        emptyContent="No clusters found"
        addUrl={routeManager.routes.openstack.createCluster.path()}
        addText="Add Cluster"
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}

export default ClusterHostGroupsListPage

const useStyles = makeStyles<Theme, { status?: string; enabled?: boolean }>((theme: Theme) => {
  const commonStyles = {
    display: 'inline-grid',
    alignItems: 'center',
    gridTemplateColumns: 'minmax(22px, max-content) 1fr',
    whiteSpace: 'nowrap' as const,
  }

  return {
    circle: {
      ...commonStyles,
      '&:before': {
        content: '""',
        display: 'inherit',
        height: 12,
        width: 12,
        borderRadius: '50%',
        backgroundColor: ({ enabled }) =>
          enabled ? theme.palette.green.main : theme.palette.red.main,
      },
    },
  }
})
